const DEVICE_ID_KEY = 'fin_device_id';
const IP_CACHE_KEY = 'fin_cached_ip';

// ─── Device ID ───────────────────────────────────────────────────────────────
export function getOrCreateDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

// ─── User Agent Parser ───────────────────────────────────────────────────────
export function parseUserAgent(ua: string): {
  deviceType: 'desktop' | 'mobile' | 'tablet';
  osName: string;
  browserName: string;
  deviceIcon: string;
  osIcon: string;
} {
  const isIpad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const isIphone = /iPhone/i.test(ua);
  const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua);
  const isAndroidMobile = /Android/i.test(ua) && /Mobile/i.test(ua);

  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (isIphone || isAndroidMobile) deviceType = 'mobile';
  else if (isIpad || isAndroidTablet) deviceType = 'tablet';

  // OS
  let osName = 'Desconhecido';
  let osIcon = 'ri-computer-line';
  if (isIphone) { osName = 'iOS'; osIcon = 'ri-apple-fill'; }
  else if (isIpad) { osName = 'iPadOS'; osIcon = 'ri-apple-fill'; }
  else if (isAndroidMobile || isAndroidTablet) { osName = 'Android'; osIcon = 'ri-android-fill'; }
  else if (/Windows/i.test(ua)) { osName = 'Windows'; osIcon = 'ri-windows-fill'; }
  else if (/Macintosh|Mac OS X/i.test(ua)) { osName = 'macOS'; osIcon = 'ri-apple-fill'; }
  else if (/Linux/i.test(ua)) { osName = 'Linux'; osIcon = 'ri-ubuntu-fill'; }

  // Browser — order matters: Edge/Opera must come before Chrome
  let browserName = 'Desconhecido';
  if (/Edg\//i.test(ua)) browserName = 'Edge';
  else if (/OPR\/|Opera\//i.test(ua)) browserName = 'Opera';
  else if (/YaBrowser/i.test(ua)) browserName = 'Yandex';
  else if (/SamsungBrowser/i.test(ua)) browserName = 'Samsung';
  else if (/CriOS\//i.test(ua)) browserName = 'Chrome';
  else if (/Chrome\//i.test(ua)) browserName = 'Chrome';
  else if (/FxiOS\//i.test(ua)) browserName = 'Firefox';
  else if (/Firefox\//i.test(ua)) browserName = 'Firefox';
  else if (/Safari\//i.test(ua)) browserName = 'Safari';

  const deviceIcon =
    deviceType === 'mobile'
      ? 'ri-smartphone-line'
      : deviceType === 'tablet'
      ? 'ri-tablet-line'
      : 'ri-computer-line';

  return { deviceType, osName, browserName, deviceIcon, osIcon };
}

// ─── IP Address (cached per browser session) ─────────────────────────────────
export async function fetchIpAddress(): Promise<string | null> {
  const cached = sessionStorage.getItem(IP_CACHE_KEY);
  if (cached) return cached;
  try {
    const res = await fetch('https://api.ipify.org?format=json', {
      signal: AbortSignal.timeout(5000),
    });
    const data = await res.json();
    const ip = data.ip ?? null;
    if (ip) sessionStorage.setItem(IP_CACHE_KEY, ip);
    return ip;
  } catch {
    return null;
  }
}

// ─── Register / Update Session ───────────────────────────────────────────────
import { supabase } from './supabase';

/**
 * Registers or updates the current device session in the database.
 * Always resets is_revoked to false — so re-login after revocation works correctly.
 * Returns true on success, false on failure.
 */
export async function registerCurrentSession(userId: string): Promise<boolean> {
  try {
    const deviceId = getOrCreateDeviceId();
    const ua = navigator.userAgent;
    const { deviceType, osName, browserName } = parseUserAgent(ua);
    const ip = await fetchIpAddress();
    const label = `${osName} · ${browserName}`;

    const { error } = await supabase.from('user_sessions').upsert(
      {
        user_id: userId,
        device_id: deviceId,
        ip_address: ip,
        user_agent: ua,
        device_type: deviceType,
        os_name: osName,
        browser_name: browserName,
        session_label: label,
        last_active_at: new Date().toISOString(),
        is_revoked: false,   // Always reset on fresh login
      },
      { onConflict: 'user_id,device_id' }
    );

    if (error) {
      console.error('[sessionDevice] registerCurrentSession upsert error:', error.message, error);
      return false;
    }

    console.log('[sessionDevice] session registered:', { userId, deviceId, osName, browserName, ip });
    return true;
  } catch (err) {
    console.error('[sessionDevice] registerCurrentSession exception:', err);
    return false;
  }
}

export async function updateLastActive(userId: string): Promise<void> {
  try {
    const deviceId = getOrCreateDeviceId();
    await supabase
      .from('user_sessions')
      .update({ last_active_at: new Date().toISOString() })
      .eq('user_id', userId)
      .eq('device_id', deviceId);
  } catch {
    // silent
  }
}

/**
 * Checks if the current device session was revoked by another device.
 * Returns true only if the row explicitly has is_revoked = true.
 * Returns false if the row doesn't exist (new device) or on any error.
 */
export async function checkSessionRevoked(userId: string): Promise<boolean> {
  try {
    const deviceId = getOrCreateDeviceId();
    const { data, error } = await supabase
      .from('user_sessions')
      .select('is_revoked')
      .eq('user_id', userId)
      .eq('device_id', deviceId)
      .maybeSingle();

    if (error) {
      console.warn('[sessionDevice] checkSessionRevoked error:', error.message);
      return false; // Fail-safe: don't kick the user if we can't check
    }

    // Only revoked if the row EXISTS and is_revoked is explicitly true
    return data !== null && data.is_revoked === true;
  } catch {
    return false;
  }
}
